import { Pagination } from '@api/entities';
import HundredBricksAPI from '../../HundredBricksAPI';
import { APIError } from '../../../api';
import { AmenitiesError, Amenity } from '../amenities';
import ManagementLogs, { ManagementLog, ManagementLogsError } from '../managementLogs';
// eslint-disable-next-line max-len
import CalculateQuoterBricks from './quoterBricks/CalculateQuoterBricks';
// eslint-disable-next-line max-len
import TraderDoesNotHasBricksReservation from './bricksReservation/TraderDoesNotHasBricksReservation';
import TraderDoesNotHasReservedBricks from './bricksReservation/TraderDoesNotHasReservedBricks';
import ReservationNotFound from './bricksReservation/ReservationNotFound';
import BestOpportunity from './bestOpportunities/bestOpportunity';
import QuoterBricksError from './quoterBricks/CalculateQuoterBricksError';
import BrickQuantityExceedsAvailable from './quoterBricks/BrickQuantityExceedsAvailable';
import OnSaleReportStatus, { OnSaleStatusReportError } from './latestOnSalePropertyStatusReport';
import { BestOpportunitiesError } from './bestOpportunities';
import { IncomePeriod, IncomeHistoryError, IncomePeriodsHistory } from './incomeHistory';
import PropertyDeveloper, { PropertyDeveloperError } from './propertyDeveloper';
import PropertyLeaseHolders, { PropertyLeaseHoldersError } from './propertyLeaseHolders';
import { OnSaleProperties, OnSaleProperty, OnSalePropertiesError, OnSalePropertiesInvalidQueryParam } from './onSaleProperty';
import { BricksReservation, BricksReservationError } from './bricksReservation';
class OnSalePropertyAPI extends HundredBricksAPI {
    async getIncomeHistory(propertyId) {
        try {
            const { data } = await this.client.get(`v2/properties/${propertyId}/dispersions?sortedBy=-createdAt&status=success`);
            return new IncomePeriodsHistory({
                incomeDispersions: data.items.map(incomePeriod => new IncomePeriod(incomePeriod)),
                totals: data.totals,
                pagination: new Pagination(data.pagination),
                count: data.count
            });
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new IncomeHistoryError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new IncomeHistoryError('Unexpected income history error', { cause: error });
            }
            else {
                throw new IncomeHistoryError(`Unexpected income history error: '${error}'`);
            }
        }
    }
    async getAmenities(propertyId) {
        try {
            const { data } = await this.client.get(`v2/properties/${propertyId}/amenities`);
            return data.items.map(amenity => new Amenity(amenity));
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new AmenitiesError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new AmenitiesError('Unexpected on sale property amenities error', { cause: error });
            }
            else {
                throw new AmenitiesError(`Unexpected on sale property amenities error: '${error}'`);
            }
        }
    }
    async getProperties(params) {
        try {
            const { data } = await this.client.get('v2/properties', { params });
            const properties = data.items.map(property => new OnSaleProperty(property));
            const pagination = new Pagination(data.pagination);
            return new OnSaleProperties({ ...data, pagination, items: properties });
        }
        catch (error) {
            if (error instanceof APIError) {
                switch (error.code) {
                    case 'PAGE_CANNOT_BE_LESS_THAN_ONE':
                        throw new OnSalePropertiesInvalidQueryParam('page', params?.page, { cause: error });
                    case 'PER_PAGE_CANNOT_BE_LESS_THAN_ONE':
                        throw new OnSalePropertiesInvalidQueryParam('perPage', params?.perPage, { cause: error });
                    case 'INVALID_PAGE':
                        throw new OnSalePropertiesInvalidQueryParam('page', params?.page, { cause: error });
                    case 'INVALID_PER_PAGE':
                        throw new OnSalePropertiesInvalidQueryParam('perPage', params?.perPage, { cause: error });
                    default:
                        throw new OnSalePropertiesError({ cause: error });
                }
            }
            else if (error instanceof Error) {
                throw new OnSalePropertiesError('Unexpected error when obtaining onsale properties', { cause: error });
            }
            else {
                throw new OnSalePropertiesError(`Unexpected error when obtaining onsale properties: '${error}'`);
            }
        }
    }
    async getPropertyManagementLogs(propertyId) {
        try {
            const { data } = await this.client.get(`v2/properties/${propertyId}/managementLogs`);
            const managementLogs = data.items.map(log => new ManagementLog(log));
            return new ManagementLogs(managementLogs);
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new ManagementLogsError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new ManagementLogsError('Unexpected error when obtaining onsale property management logs', { cause: error });
            }
            else {
                throw new ManagementLogsError(`Unexpected error when obtaining onsale property management logs: '${error}'`);
            }
        }
    }
    async getLatestOnSalePropertyStatusReport(propertyId) {
        try {
            const { data } = await this.client.get(`v2/properties/${propertyId}/statusReports/latest`);
            return new OnSaleReportStatus(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new OnSaleStatusReportError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new OnSaleStatusReportError('Unexpected error while retrieving on sale property status report', { cause: error });
            }
            else {
                throw new OnSaleStatusReportError(`Unexpected error while retrieving on sale property status report: '${error}'`);
            }
        }
    }
    async getBestOpportunities() {
        try {
            const { data } = await this.client.get('v2/properties/opportunities');
            return data.items.map(item => new BestOpportunity(item));
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new BestOpportunitiesError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new BestOpportunitiesError('Unexpected On sale Opportunity error', {
                    cause: error
                });
            }
            else {
                throw new BestOpportunitiesError(`Unexpected On sale Opportunity error: '${error}'`);
            }
        }
    }
    async getPropertyDeveloper(propertyId) {
        try {
            const { data } = await this.client.get(`v2/properties/${propertyId}/developer`);
            return new PropertyDeveloper(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new PropertyDeveloperError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new PropertyDeveloperError('Unexpected error obtaining developer property', {
                    cause: error
                });
            }
            else {
                throw new PropertyDeveloperError(`Unexpected error obtaining developer property: '${error}'`);
            }
        }
    }
    async getPropertyLeaseHolders(propertyId) {
        try {
            const { data } = await this.client.get(`v2/properties/${propertyId}/leaseholders`);
            return new PropertyLeaseHolders(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new PropertyLeaseHoldersError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new PropertyLeaseHoldersError('Unexpected error obtaining property leaseholders', {
                    cause: error
                });
            }
            else {
                throw new PropertyLeaseHoldersError(`Unexpected error obtaining property leaseholders: '${error}'`);
            }
        }
    }
    async calculateQuotation(propertyId, bricksQuantity) {
        try {
            const { data } = await this.client.get(`v1/properties/${propertyId}/quotations/calculate?bricksQuantity=${bricksQuantity}`);
            return new CalculateQuoterBricks(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                if (error.code === 'BRICK_QUANTITY_EXCEEDS_AVAILABLE') {
                    throw new BrickQuantityExceedsAvailable();
                }
                else {
                    throw new QuoterBricksError({ cause: error });
                }
            }
            else if (error instanceof Error) {
                throw new QuoterBricksError('Unexpected quoter bricks error', {
                    cause: error
                });
            }
            else {
                throw new QuoterBricksError(`Unexpected quoter bricks error: '${error}'`);
            }
        }
    }
    async reserveBricks(propertyId, bricksQuantity) {
        try {
            const { data } = await this.client.post(`v1/properties/${propertyId}/bricks/reserve`, { bricksQuantity });
            return new BricksReservation(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                if (error.code === 'RESERVATION_NOT_FOUND') {
                    throw new ReservationNotFound();
                }
                else {
                    throw new BricksReservationError({ cause: error });
                }
            }
            else if (error instanceof Error) {
                throw new BricksReservationError('Unexpected reservation bricks error', {
                    cause: error
                });
            }
            else {
                throw new BricksReservationError(`Unexpected reservation bricks error: '${error}'`);
            }
        }
    }
    async getReservation() {
        try {
            const { data } = await this.client.get('v1/traders/me/bricks/reserved');
            return new BricksReservation(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                if (error.code === 'TRADER_DOES_NOT_HAS_RESERVED_BRICKS') {
                    throw new TraderDoesNotHasReservedBricks();
                }
                else {
                    throw new BricksReservationError({ cause: error });
                }
            }
            else if (error instanceof Error) {
                throw new BricksReservationError('Unexpected reservation bricks error', {
                    cause: error
                });
            }
            else {
                throw new BricksReservationError(`Unexpected reservation bricks error: '${error}'`);
            }
        }
    }
    async releaseReservation() {
        try {
            const { data } = await this.client.post('v1/properties/bricks/releaseReserved');
            return new BricksReservation(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                if (error.code === 'TRADER_DOES_NOT_HAS_BRICKS_RESERVATION') {
                    throw new TraderDoesNotHasBricksReservation();
                }
                else {
                    throw new BricksReservationError({ cause: error });
                }
            }
            else if (error instanceof Error) {
                throw new BricksReservationError('Unexpected reservation bricks error', {
                    cause: error
                });
            }
            else {
                throw new BricksReservationError(`Unexpected reservation bricks error: '${error}'`);
            }
        }
    }
}
export default OnSalePropertyAPI;
